/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { baseApiService } from './base'

const baseURL = '/api/documenttypetemplates'
const baseURL_v2 = '/api/v2/documenttypetemplates'

export interface AllOptionsForPicker {
  id: number
  name: string
}

export interface AllOptionsForAsyncPicker {
  elements: AllOptionsForPicker[]
  totalElements: number
}

type GetAllForTableParamsType = {
  pageSize?: number
  pageStart?: number
  sortBy?: string
  sortDesc?: boolean
  id?: number[]
}

interface CreateDocumentTypeRequestType {
  allowBlindingStatusChange?: boolean
  id?: number
  workflowTemplate?: number
  templateFileId?: number
  signatureTemplateFileId?: number
  watermarkIds?: number[]
  expirationTimeMonthsEnabled?: boolean
  isPeriodicReviewCustomDateEnabled?: boolean
  customNextDateMetadataTemplate?: Date
  customLastDateMetadataTemplate?: Date
  requiredMetadataTemplates?: number[]
  metadataTemplates?: number[]
  amendmentsEnabled?: boolean
  keepDraftVersionsAfterApproval?: boolean
  isInitiatorReviewerOverlapCheckEnabled?: boolean
  effectiveTimeDaysEnabled?: boolean
  allowPropertiesChangeDuringReview?: boolean
  allowPropertiesChangeDuringEdit?: boolean
  requireRejectReason?: boolean
  requireJustification?: boolean
  allowOverridesDuringCR?: boolean
  addSignaturePageToFront?: boolean
  addSignaturePage?: boolean
  requireExpirationDate?: boolean
  description?: string
  name?: string
  expirationTimeMonths?: number
  effectiveTimeDays?: number
  autonamingEnabled?: boolean
  autonamingSeqNo?: number
  autonamingTemplate?: string
  customEffectiveStateLabel?: string
  _customEffectiveStateLabelText?: string
  _customEffectiveStateLabelType: string
}

interface User {
  id: number
  name: string
}

export interface DocumentType {
  id: number
  name: string
  description?: string
  system?: boolean
  createdAt?: number
  createdBy?: User
  updatedAt?: number
  updatedBy?: User
}

class DocumentTypesApiService {
  /**
   * Fetch all document types (legacy getAllDocumentTypes)
   * @param params Optional query params (default: {sortBy: 'name', sortDesc: false})
   * @returns Promise<any> (raw API response)
   * Logic matches legacy AngularJS: apiWithLoader.one(baseURL).get(params)
   */
  async getAllDocumentTypes(params?: Record<string, any>): Promise<any> {
    const query = params ? params : { sortBy: 'name', sortDesc: false }
    // Use baseURL (not v2) to match legacy
    return baseApiService.fetch(baseURL, { params: query })
  }

  async getAllForTable(params: GetAllForTableParamsType) {
    params = params ? params : { sortBy: 'name', sortDesc: false }
    return baseApiService.fetch(baseURL_v2, { params })
  }

  // async getAllForPicker(hideLoader: boolean) {
  async getAllForPicker(): Promise<AllOptionsForPicker[]> {
    return new Promise<AllOptionsForPicker[]>((resolve, reject) => {
      baseApiService.fetch(baseURL + '/all').then((data) => {
        const localData = data as AllOptionsForPicker[]
        if (localData?.length) {
          localData.sort((prev, next) => prev.name.toLowerCase().localeCompare(next.name.toLowerCase()))
          resolve(localData)
        } else {
          reject()
        }
      })
    })
  }

  async getAllForAsyncPicker(searchTerm: string, ids: number[]) {
    const params: { sortBy: string; sortDesc: boolean; name: string; pageSize: number; id?: number[] } = {
      sortBy: 'NAME',
      sortDesc: false,
      name: searchTerm,
      pageSize: 200
    }

    const searchByIds = ids && ids.length

    if (searchByIds) {
      params.id = ids
    }

    return baseApiService
      .fetch(baseURL_v2 + (searchByIds ? '' : '/picker'), {
        method: 'GET',
        params
      })
      .then((response) => {
        const responseData = response as AllOptionsForAsyncPicker
        return responseData.elements.map((item: { id: number; name: string }) => {
          return { value: item?.id, label: item?.name }
        })
      })
  }

  async getById(typeId: number, useVersion1?: boolean) {
    // VERIFY THE USE OF typeId HERE
    return baseApiService.fetch((useVersion1 ? baseURL : baseURL_v2) + '/' + typeId)
  }

  async getOptionsForAsyncPicker(search: string, pageStart: number) {
    const pageSize = 25
    const params = {
      name: search || null,
      pageStart, // Pass page number directly (0, 1, 2, ...)
      pageSize
    }

    return baseApiService
      .fetch(baseURL_v2 + '/picker', {
        method: 'GET',
        params
      })
      .then((response: any) => {
        const elements = response?.elements || []
        const totalElements = response?.totalElements || 0

        // Calculate hasMore: (pageStart + 1) * pageSize < totalElements
        // Example: page 0 → (0+1)*25=25 < 100 → true
        // Example: page 3 → (3+1)*25=100 < 100 → false (last page)
        const hasMore = elements.length > 0 && (params.pageStart + 1) * params.pageSize < totalElements

        return {
          options: elements.map((item: { id: number; name: string }) => ({
            value: item.id,
            label: item.name
          })),
          hasMore
        }
      })
  }

  // async create(data: CreateDocumentTypeRequestType, hideLoader: boolean) {
  async create(data: CreateDocumentTypeRequestType) {
    return baseApiService.fetch(baseURL, {
      method: 'POST',
      body: { ...data }
    })
  }

  async update(typeId: number, data: CreateDocumentTypeRequestType) {
    // VERIFY THE USE OF typeId HERE
    return baseApiService.fetch(baseURL + '/' + typeId, {
      method: 'PUT',
      body: { ...data }
    })
  }

  // async remove(typeId: number, hideLoader: boolean) {
  async remove(typeId: number) {
    return baseApiService.fetch(baseURL + '/' + typeId, {
      method: 'DELETE'
    })
  }

  // async documentUsage(typeId: number, hideLoader: boolean) {
  async documentUsage(typeId: number) {
    return baseApiService.fetch(baseURL + '/' + typeId + '/usage')
  }

  async getAll(params?: Record<string, any>): Promise<any> {
    const query = params ? params : { sortBy: 'name', sortDesc: false }
    // Use baseURL (not v2) to match legacy
    return baseApiService.fetch(baseURL, { params: query })
  }
}

export const documentTypesApiService = new DocumentTypesApiService()
